import {DrupalNode} from "next-drupal";
import {MutableRefObject, useContext, useEffect, useState} from "react";
import {NodeProductDocumentButton} from "../molecules/product-document-button";
import {Button} from "../atoms/button";
import classNames from "classnames";
import {Dictionary, DrupalLink} from "../../types/hygiena-types";
import {DictionaryContext} from "../../context/dictionary-context";
import {StoreContext, StoreContextType} from "@/context/store-context";
import {getProductVariations} from "@/lib/get-product-variations";
import {CONTACT_PORTAL_ID} from "@/lib/constants";

/**
 * Displays the product node documentation buttons.
 *
 * @param node
 *   The product node.
 * @param contact
 *   Will display the contact button if true.
 * @param videoRef
 *   The video ref.
 * @param className
 *   An optional classname.
 * @param hoverCallback
 *   An optional callback to run when hovering on an item.
 * @param viewMode
 *   An optional view mode i.e. subproduct.
 * @constructor
 */
export function NodeProductDocumentButtonGrid({node, contact, videoRef, className, hoverCallback, viewMode, includeOrderOnline}: {
  node: DrupalNode,
  contact?: boolean,
  videoRef?: MutableRefObject<HTMLDivElement|null>,
  className?: string,
  hoverCallback?: (boolean) => any,
  viewMode?: string,
  includeOrderOnline?: boolean,
}) {
  const [isClient, setIsClient] = useState(false);
  const { enableCart } = useContext<StoreContextType>(StoreContext);
  const productManual = node?.field_product_product_manual;
  const productBrochure = node?.field_product_brochure;
  const productSheet = node?.field_product_product_sheet;
  const safetyDataSheet = node?.field_product_safety_data_sheet;
  const readyReferenceSheet = node?.field_product_quick_guide;
  const productVideo = node?.field_product_video?.[0];
  const softwarePatches = node?.field_product_software_patches;
  const orderOnline = node?.field_product_order_online_link;
  const orderOnlineAus = node?.field_product_order_online_aus;
  const t = useContext<Dictionary>(DictionaryContext);
  const sdsScroll = node?.field_product_sds_scroll ?? false;
  const refGuideScroll = node?.field_product_ref_guide_scroll ?? false;
  const productManualScroll = node?.field_product_manual_scroll ?? false;
  const brochureScroll = node?.field_product_brochure_scroll ?? false;
  const productSheetScroll= node?.field_product_sheet_scroll ?? false;
  const variations = getProductVariations(node);
  const hasOrderOnlineLinks = variations.filter(variation => variation.productNumbers.filter(item => item.ausStoreLink || item.usStoreLink).length > 0)?.length > 0;

  useEffect(() => {
    setIsClient(true);
  }, []);

  const totalButtons = [
    productManual,
    productBrochure,
    productSheet,
    safetyDataSheet,
    readyReferenceSheet,
    productVideo,
    softwarePatches,
    orderOnline,
  ].filter(item => {
    if (Array.isArray(item)) return item.filter(doc => doc?.type && doc?.type !== "unknown")?.length
    return item;
  })?.length;

  function jumpToContactus() {
    const form = document.getElementById(CONTACT_PORTAL_ID);
    if (form) {
      form.scrollIntoView({behavior: 'smooth'})
    }
  }

  /**
   * Scrolls to the order online section if available.
   */
  const scrollToVariations = async () => {
    const form = document.getElementById("order-online-section");
    if (form) {
      form.scrollIntoView({behavior: 'smooth'})
    }
  }

  /**
   * Click the reference thumbnail, if present.
   */
  function videoButtonClick() {
    if (videoRef && videoRef?.current) {
      videoRef.current.click();
    }
  }

  // @todo we can put the identifiers into an array and loop them to build all of this out, no
  //   need for all of the repetitive code - DRY. Clean this up!
  return (
    <div className={classNames(
      "grid gap-2 mb-6 auto-rows-1fr md:grid-cols-2",
      className ?? "",
      {"grid-cols-1": totalButtons === 0},
      {"grid-cols-2": totalButtons >= 1},
    )}>
      {productVideo && (
        <Button callback={videoButtonClick} icon={"play-circle"} className="py-1.5 px-1 w-full h-full max-md:text-sm" color={"primary"} hover={{color: "primary"}} responsiveIcon={true} height={22} width={22}>{`${t?.all?.product_video}`}</Button>
      )}
      <NodeProductDocumentButton field={productBrochure} label={`${t?.all?.product_brochure}`} scrollToLibrary={brochureScroll} scrollLibraryClass={"brochure"} callback={hoverCallback}/>
      <NodeProductDocumentButton field={productSheet} label={`${t?.all?.product_sheet}`} scrollToLibrary={productSheetScroll} scrollLibraryClass={"product-sheet"} callback={hoverCallback}/>
      <NodeProductDocumentButton field={productManual} label={`${t?.all?.product_instructions}`} scrollToLibrary={productManualScroll} scrollLibraryClass={"instructions"} callback={hoverCallback}/>
      <NodeProductDocumentButton field={readyReferenceSheet} label={`${t?.all?.reference_guide}`} scrollToLibrary={refGuideScroll} scrollLibraryClass={"ref-guide"} callback={hoverCallback}/>
      {!(node?.field_product_class && viewMode === "subproduct") && (
        <NodeProductDocumentButton field={safetyDataSheet} label={`${t?.all?.safety_data_sheet}`} scrollToLibrary={sdsScroll} scrollLibraryClass={"sds"} callback={hoverCallback}/>
      )}
      <NodeProductDocumentButton field={softwarePatches} label={`${t?.all?.software_and_patches}`} callback={hoverCallback} absolute={false} defaultList={true}/>
      {/* Us Order Online Only */}
      {(includeOrderOnline && hasOrderOnlineLinks) && (
        <Button callback={scrollToVariations} position={"right"} className="shop-online py-1.5 px-1 max-md:text-center h-full w-full bg-white" height={22} width={22} invert={true} color={"primary"} hover={{color: "primary"}}>{t?.all?.order_online}</Button>
      )}
      {contact && (
        <Button callback={jumpToContactus} className="py-1.5 px-1 bg-white w-full h-full" color={"primary"} hover={{color: "primary"}} invert={true}>{`${t?.all?.request_more_info}`}</Button>
      )}
    </div>
  )
}

